import { isEqual } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, take, takeUntil, tap } from 'rxjs/operators';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { TransactionService } from '../../../_core/api/transaction.service';
import { INPUT_TYPE } from '../../../_core/contants/Contribute';
import { getRangePatterns } from '../../../_core/contants/rangePatterns';
import { SERVER_ERROR } from '../../../_core/contants/ServerError';
import { CustomValidators } from '../../../_core/helpers/custom-validators.helpers';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { CustomControl } from '../../../_core/models/CustomForms.model';
import { GlobalConfigService } from '../../../_core/services/global-config.service';
import { ModalsService } from '../../../_core/services/modals.service';
import { Configuration } from '../../../app.constants';
import { RecurringTransfer } from '../../../pages/transaction/manage-recurring/models';
import { BalanceCurrency } from '../../../pages/transaction/transaction-form/transaction-form.const';
import { RECURRENCE_RANGE_END } from '../../recurring-form/recurring-form.models';
import { createRangeEndValidator } from '../../recurring-form/recurring-form.validator';

@Component({
  selector: 'app-edit-transaction',
  templateUrl: './edit-transaction.component.html',
  styleUrls: ['./edit-transaction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTransactionComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  inputTypes = INPUT_TYPE;

  startDateDisabled: boolean;
  instanceName: string;
  tempCharityInstruction: string;
  tempAdditionalNotes: string;
  params = this.modalsService.params;
  modalLoading = true;
  grantWarning = false;
  showSummary = false;
  formTouched = false;

  initialValues = {
    recurrencePattern: this.params.transaction.recurrencePattern,
    startDate: this.params.transaction.startDate,
    endDate: this.params.transaction.endDate,
    amount: this.params.transaction.amountInCents / 100,
    currencySymbol: this.params.transaction.currencySymbol,
    charityInstruction: this.params.transaction.charityInstruction,
    additionalNotes: this.params.transaction.additionalNotes,
  };

  updatedValues = {
    recurrencePattern: null,
    startDate: null,
    endDate: null,
    amount: null,
    charityInstruction: null,
    additionalNotes: null,
  };

  dateFormat$ = this.globalConfigService.globalConfig$.pipe(map(({ dateFormat }) => dateFormat));
  specialInstructionsAvailable$ = this.globalConfigService.globalConfig$.pipe(
    map(({ specialInstructionsAvailable }) => specialInstructionsAvailable && this.params.isGrant)
  );

  editTransactionForm = new FormGroup(
    {
      recurrence: new FormGroup({
        recurrencePattern: new CustomControl({
          value: this.params.transaction.recurrencePattern,
        }),
        recurrenceRange: new FormGroup(
          {
            rangeOption: new CustomControl({
              value: RECURRENCE_RANGE_END.ENDBY,
            }),
            startDate: new CustomControl({
              value: new Date(this.params.transaction.startDate),
            }),
            endDate: new CustomControl({
              value: new Date(this.params.transaction.endDate),
            }),
            rangeOccurrences: new CustomControl({
              value: 2,
            }),
          },
          { validators: [createRangeEndValidator()] }
        ),
      }),
      amount: new CustomControl({
        value: this.params.transaction.amountInCents / 100,
        label: 'DONATE_CONTRIBUTION_LABELS_AMOUNT',
        validation: [
          ['required', 'FORM_VALIDATORS_AMOUNT_REQUIRED'],
          ['positiveNumber', 'FORM_VALIDATORS_AMOUNT_POSITIVE'],
          ['maxAmountAllowed', 'FORM_VALIDATORS_AMOUNT_EXCEEDS_BALANCE'],
          ['maxValue', 'FORM_VALIDATORS_AMOUNT_MAX_EXCEEDED'],
        ],
        max: null,
        helperData: {
          minValue: null,
          isVerifying: false,
          showError: false,
        },
      }),
      currency: new CustomControl({
        value: this.params.transaction.currencySymbol,
        hasAuxControl: true,
      }),
      showCharityInstruction: new CustomControl({
        value: this.params.transaction.charityInstruction || false,
        label: 'GRANT_SPECIAL_INSTRUCTIONS_CHARITY',
      }),
      charityInstruction: new CustomControl({
        placeholder: 'GRANT_CHARITY_INSTRUCTIONS',
        validation: [
          ['required', 'FORM_VALIDATORS_CHARITY_INSTRUCTIONS_REQUIRED'],
          ['minLength', 'FORM_VALIDATORS_CHARITY_INSTRUCTIONS_MIN'],
          ['maxLength', 'FORM_VALIDATORS_CHARITY_INSTRUCTIONS_MAX'],
        ],
        value: this.params.transaction.charityInstruction || '',
      }),
      showAdditionalNotes: new CustomControl({
        value: this.params.transaction.additionalNotes || false,
        label: 'GRANT_SPECIAL_INSTRUCTIONS',
      }),
      additionalNotes: new CustomControl({
        placeholder: 'GRANT_ADDITIONAL_NOTES',
        validation: [
          ['required', 'FORM_VALIDATORS_ORGANIZATION_INSTRUCTIONS_REQUIRED'],
          ['minLength', 'FORM_VALIDATORS_ORGANIZATION_INSTRUCTIONS_MIN'],
          ['maxLength', 'FORM_VALIDATORS_ORGANIZATION_INSTRUCTIONS_MAX'],
        ],
        value: this.params.transaction.additionalNotes || '',
      }),
    },
    { validators: [this.createRangeValidator()] }
  );

  get amount(): CustomControl {
    return this.editTransactionForm.controls.amount as CustomControl;
  }

  get currency(): CustomControl {
    return this.editTransactionForm.controls.currency as CustomControl;
  }

  get showCharityInstruction(): CustomControl {
    return this.editTransactionForm.controls.showCharityInstruction as CustomControl;
  }

  get charityInstruction(): CustomControl {
    return this.editTransactionForm.controls.charityInstruction as CustomControl;
  }

  get showAdditionalNotes(): CustomControl {
    return this.editTransactionForm.controls.showAdditionalNotes as CustomControl;
  }

  get additionalNotes(): CustomControl {
    return this.editTransactionForm.controls.additionalNotes as CustomControl;
  }

  constructor(
    private globalConfigService: GlobalConfigService,
    private modalsService: ModalsService,
    private transactionService: TransactionService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private config: Configuration
  ) {}

  ngOnInit(): void {
    this.instanceName = this.config.name;
    this.showSummary = false;
    this.tempCharityInstruction = this.params.transaction.charityInstruction || '';
    this.tempAdditionalNotes = this.params.transaction.additionalNotes || '';
    this.patchCurrency();
    this.setFundAvailableAmount();
    this.setFormListeners();
    this.setValidations();
    this.params.isRenew ? this.setupRenew() : this.disableStartDate();
    this.checkRecurrencePatternConfig();
    this.checkMinimumAmount(!this.params.isGrant);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  setValidations(): void {
    const minLength = 10;
    const maxLength = 250;

    [
      { control: this.charityInstruction, toggle: 'showCharityInstruction' },
      { control: this.additionalNotes, toggle: 'showAdditionalNotes' },
    ].forEach(({ control, toggle }) => {
      control.clearValidators();

      const validators = [CustomValidators.minLength(minLength), CustomValidators.maxLength(maxLength)];

      if (this.editTransactionForm.controls[toggle].value) validators.push(CustomValidators.required);

      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }

  checkRecurrencePatternConfig(): void {
    const control = this.editTransactionForm.get('recurrence.recurrencePattern');
    const currentPattern = this.params.transaction.recurrencePattern;
    const globalPatterns = this.globalConfigService.getGlobalConfig().recurrencePattern;
    !globalPatterns.includes(currentPattern) && control.patchValue(null);
  }

  disableStartDate(): void {
    const currentDate = new Date();
    const formStartDate = this.editTransactionForm.get('recurrence.recurrenceRange.startDate').value;

    this.startDateDisabled = moment(formStartDate).isSameOrBefore(moment(currentDate), 'date');
    this.startDateDisabled && this.editTransactionForm.get('recurrence.recurrenceRange.startDate').disable();
  }

  // Renew mode: revive a completed grant. The original start date is in the
  // past, so reset it to today (kept editable so the user can push it forward)
  // and clear the end date to force the user to pick a new one.
  setupRenew(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.startDateDisabled = false;
    this.formTouched = true;

    const range = this.editTransactionForm.get('recurrence.recurrenceRange') as FormGroup;
    const startDate = range.get('startDate');
    startDate.enable({ emitEvent: false });
    startDate.setValue(today, { emitEvent: false });
    range.get('rangeOption').setValue(RECURRENCE_RANGE_END.ENDBY, { emitEvent: false });
    range.get('endDate').setValue(null, { emitEvent: false });
    range.updateValueAndValidity({ emitEvent: false });
  }

  setFundAvailableAmount(): void {
    const transaction: RecurringTransfer = this.params.transaction;

    this.transactionService
      .getCurrencies(transaction.senderCseg5)
      .pipe(
        tap(() => (this.modalLoading = true)),
        map((currencies: BalanceCurrency[]) => currencies.find((currency) => +currency.id === transaction.currency)),
        map(({ availableAmountFundCash }) => availableAmountFundCash)
      )
      .subscribe((availableAmount) => {
        this.setAvailableAmount(availableAmount);
        this.modalLoading = false;
        this.cdr.markForCheck();
      });
  }

  createRangeValidator(): ValidatorFn {
    return (form: FormGroup): ValidationErrors | null => {
      let rangeError: { [key: string]: boolean } | null = null;

      const amount = form.get('amount');
      const { recurrencePattern, recurrenceRange } = form.get('recurrence').value;
      const { endDate, rangeOccurrences, rangeOption } = recurrenceRange;
      // On renew the form's start date (today) is the effective start, not the
      // original past start date stored on the transaction.
      const startDate = this.params.isRenew
        ? new Date(form.get('recurrence.recurrenceRange.startDate')?.value || this.params.transaction.startDate)
        : new Date(this.params.transaction.startDate);

      this.grantWarning = false;

      if (!recurrencePattern) return { required: true };

      if (rangeOption === RECURRENCE_RANGE_END.NOEND) rangeError = null;

      const availableValue = amount['helperData'].availableValue;

      if (rangeOption === RECURRENCE_RANGE_END.ENDAFTER) {
        rangeError = amount.value * rangeOccurrences > availableValue ? { rangeError: true } : null;
      }

      if (rangeOption === RECURRENCE_RANGE_END.ENDBY) {
        rangeError = amount.value * getRangePatterns(startDate, endDate)[recurrencePattern] > availableValue ? { rangeError: true } : null;
      }

      if (rangeError || rangeOption === RECURRENCE_RANGE_END.NOEND) {
        this.grantWarning = true;
        return null;
      }

      return rangeError;
    };
  }

  setFormListeners(): void {
    this.amount.valueChanges.pipe(debounceTime(300), takeUntil(this._destroy$)).subscribe(() => {
      this.checkMinimumAmount(!this.params.isGrant);
      if (this.amount.value < 1) this.amount.patchValue(0, { emitEvent: false });
      this.cdr.markForCheck();
    });

    // Workaround for form not updating .pristine/.dirty properties
    this.editTransactionForm.valueChanges
      .pipe(
        skip(1),
        distinctUntilChanged((x, y) => !isEqual(x, y)),
        take(1)
      )
      .subscribe(() => {
        this.formTouched = true;
        this.cdr.markForCheck();
      });

    const specialInstructionChanges = (control: CustomControl) => {
      control.setValue('');
      this.setValidations();
    };

    this.showCharityInstruction.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => specialInstructionChanges(this.charityInstruction));

    this.showAdditionalNotes.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => specialInstructionChanges(this.additionalNotes));
  }

  checkMinimumAmount(isDistribution: boolean): void {
    if (!this.amount.value) return;

    this.amount.helperData.showError = false;
    this.amount.helperData.isVerifying = true;

    if (isDistribution) {
      const payload = { amount: this.amount.value, currency: this.params.transaction.currency };

      this.transactionService
        .calculateGrant(payload)
        .subscribe({
          next: () => (this.amount.helperData.isVerifying = false),
          error: (error) => {
            if (error.status === 409) {
              if (error.error.message === SERVER_ERROR.INVALID_CURRENCY) {
                this.translateService.get('TOASTER_INVALID_CURRENCY').subscribe((text: string) => alert(text));
              }
              this.amount.helperData.minValue = error.error.minimumGrantAmount;
              this.amount.helperData.showError = true;
            }
          },
        })
        .add(() => {
          this.amount.helperData.isVerifying = false;
          this.cdr.markForCheck();
        });
    } else {
      const { minimumGrantAmount } = this.globalConfigService.getGlobalConfig();
      if (minimumGrantAmount && this.amount.value < minimumGrantAmount) {
        this.amount.helperData.minValue = minimumGrantAmount;
        this.amount.helperData.showError = true;
      }
    }
    this.amount.helperData.isVerifying = false;
  }

  setAvailableAmount(amount: number): void {
    this.amount.helperData.availableValue = amount ? SharedMethodsHelpers.computeAmountToDisplay(amount) : 0;
    this.amount.setValidators([
      CustomValidators.maxAmountAllowed(this.amount.helperData.availableValue),
      CustomValidators.required,
      CustomValidators.positiveNumber,
    ]);
  }

  onNextOrSubmit(): void {
    if (this.showSummary) {
      const payload = {
        id: this.params.transaction.id,
        recurrencePattern: this.updatedValues.recurrencePattern,
        startDate: this.updatedValues.startDate,
        endDate: this.updatedValues.endDate,
        amountInCents: this.updatedValues.amount * 100,
        currency: this.params.transaction.currency,
        currencySymbol: this.params.transaction.currencySymbol,
        charityInstruction: this.updatedValues.charityInstruction,
        additionalNotes: this.updatedValues.additionalNotes,
      };

      this.modalsService.emitResponse(payload);
      this.closeModal();
    } else {
      this.updatedValues = {
        ...this.updatedValues,
        amount: this.editTransactionForm.value.amount,
        startDate: moment(this.editTransactionForm.value.recurrence.recurrenceRange.startDate || this.params.transaction.startDate).valueOf(),
        endDate: moment(this.editTransactionForm.value.recurrence.recurrenceRange.endDate).valueOf(),
        recurrencePattern: this.editTransactionForm.value.recurrence.recurrencePattern,
        charityInstruction: this.editTransactionForm.value.charityInstruction,
        additionalNotes: this.editTransactionForm.value.additionalNotes,
      };
      this.showSummary = true;
    }
  }

  patchCurrency(): void {
    this.currency.patchValue(this.params.transaction.currencySymbol);
    this.currency.auxControl.patchValue(this.params.transaction.currencySymbol);
    this.currency.auxControl.disable();
  }

  onCancel(): void {
    if (this.showSummary) {
      this.showSummary = false;
    } else {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.modalsService.closeModal();
  }
}
